import { SimpleTruncate } from "@dropbox/dash-component-library";
import { Badge } from "@dropbox/dig-components/badge";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddCircleLine,
  AddToLibraryLine,
  AiChatLine,
  DropboxLine,
  EditLine,
  PersonMultipleLine,
  StacksFill,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { getTimeOfDayGreetingWithName } from "@mirage/mosaics/StartPage/Calendar/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useName } from "@mirage/service-auth/useName";
import {
  useWorkflowAgentRecipientEnabled,
  useWorkflowAgents,
  useWorkflowAgentsEnabled,
} from "@mirage/service-conversation/hooks/useWorkflowAgents";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { IconButtonWithTooltip } from "@mirage/shared/icons/IconButtonWithTooltip";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import { onKeyDownCommitFn } from "@mirage/shared/util/on-key-down";
import i18n, { meticulousRedactedStringClass } from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import classNames from "classnames";
import { memo, useCallback } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import styles from "./EmptyChatInstructions.module.css";

import { useChatRoute } from "@mirage/mosaics/ConversationAssistant/hooks/useChatRoute";
import {
  CtaSource,
  type WorkflowAgentParams,
} from "@mirage/service-conversation/router_state_types";
import type { WorkflowAgent } from "@mirage/service-conversation/types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { EMOJI_CLEARED_SENTINEL_VALUE } from "@mirage/shared/util/constants";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";

export type ClickableEmptyChatInstructionsOption =
  | "write"
  | "chat_with_dash"
  | "create-ai-tool"
  | "library"
  | "workflow-agent";
type EmptyChatInstructionsOption =
  | "write"
  | "chat_with_dash"
  | "create-ai-tool"
  | "library"
  | "workflow-agent";

interface EmptyChatInstructionsProps {
  chatWithDashEnabled?: boolean;
  onClickOption: (option: ClickableEmptyChatInstructionsOption) => void;
}
export const EmptyChatInstructions = memo(
  ({
    chatWithDashEnabled = true,
    onClickOption,
  }: EmptyChatInstructionsProps) => {
    const { firstName } = useName();
    const isSmallSize = useIsSmallSizeForSidebar();
    const isDesktop = EnvCtx.surface === "desktop";
    const isWorkflowAgentsEnabled = useWorkflowAgentsEnabled();
    const isWorkflowAgentRecipientEnabled = useWorkflowAgentRecipientEnabled();
    return (
      <div
        className={classNames(styles.EmptyChatInstructions, {
          [styles.Mobile]: isSmallSize,
          [styles.Desktop]: isDesktop,
        })}
      >
        <div
          className={classNames(styles.TitleContainer, {
            [styles.TitleContainerMobile]: isSmallSize,
            [styles.WorkflowAgentTitleContainer]: isWorkflowAgentsEnabled,
          })}
        >
          <Box
            borderWidth="1"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ width: "48px", height: "48px" }}
          >
            <UIIcon
              className={styles.EmptyChatIcon}
              size="large"
              src={DropboxLine}
            />
          </Box>
          <Title
            size="medium"
            weightVariant="emphasized"
            className={styles.Title}
          >
            {getTimeOfDayGreetingWithName(firstName)}
          </Title>
          <Title
            size="medium"
            weightVariant="emphasized"
            className={styles.Subtitle}
          >
            {i18n.t("assistant_empty_chat_subtitle")}
          </Title>
        </div>
        <div
          className={classNames(styles.EmptyChatInstructionsCards, {
            [styles.MobileCards]: isSmallSize,
          })}
        >
          {getCardOptions(isWorkflowAgentsEnabled).map((option) => {
            const isComingSoon =
              !chatWithDashEnabled && option === "chat_with_dash";
            return (
              <EmptyChatInstructionCard
                key={option}
                option={option}
                isComingSoon={isComingSoon}
                onClickOption={onClickOption}
                isMobileSize={isSmallSize}
                withCardAccessories={
                  isComingSoon && (
                    <Badge size="xsmall" over="base">
                      {i18n.t("coming_soon_label")}
                    </Badge>
                  )
                }
              />
            );
          })}
        </div>
        {isWorkflowAgentRecipientEnabled && (
          <WorkflowAgentsList isSmallSize={isSmallSize} />
        )}
      </div>
    );
  },
);
EmptyChatInstructions.displayName = "EmptyChatInstructions";

interface EmptyChatInstructionCardProps {
  option: EmptyChatInstructionsOption;
  isComingSoon?: boolean;
  onClickOption: (option: ClickableEmptyChatInstructionsOption) => void;
  isMobileSize: boolean; // New prop
  agent?: WorkflowAgent;
  withCardAccessories?: React.ReactNode;
}
export const EmptyChatInstructionCard = memo(
  ({
    option,
    isComingSoon,
    onClickOption,
    isMobileSize,
    agent,
    withCardAccessories,
  }: EmptyChatInstructionCardProps) => {
    const {
      title,
      description,
      icon,
      color,
      borderVariant: defaultBorderVariant,
    } = getCardRenderInfo(option, agent);
    const handleClick = useCallback(() => {
      if (isComingSoon) {
        // no-op if disabled
        return;
      }
      onClickOption(option);
    }, [isComingSoon, onClickOption, option]);
    const borderVariant = isComingSoon ? "coming_soon" : defaultBorderVariant;

    // detect whether `icon` is an emoji or an icon component
    const isIconSentinel = icon === EMOJI_CLEARED_SENTINEL_VALUE || icon === "";
    const isIconEmoji = typeof icon === "string" && !isIconSentinel;

    return (
      <div
        className={classNames(
          styles.EmptyChatInstructionCard,
          meticulousRedactedStringClass,
          {
            [styles.BorderVariantNormal]: borderVariant === "normal",
            [styles.BorderVariantDotted]: borderVariant === "dotted",
            [styles.BorderVariantComingSoon]: borderVariant === "coming_soon",
            [styles.Clickable]: borderVariant !== "coming_soon",
            [styles.MobileCard]: isMobileSize,
          },
        )}
        onClick={handleClick}
        onKeyDown={onKeyDownCommitFn(handleClick)}
        tabIndex={0}
        role="button"
      >
        {!!withCardAccessories && (
          <div className={styles.CardAccessories}>{withCardAccessories}</div>
        )}
        {isIconEmoji ? (
          <Text
            size="xlarge"
            className={classNames(styles.CardIconEmoji, {
              [styles.MobileCardIcon]: isMobileSize,
            })}
          >
            {icon}
          </Text>
        ) : (
          <UIIcon
            className={classNames(styles.CardIcon, {
              [styles.MobileCardIcon]: isMobileSize,
            })}
            src={isIconSentinel ? StacksFill : icon}
            size="large"
            color={color}
          />
        )}
        <Title
          size="small"
          weightVariant="emphasized"
          className={styles.CardTitle}
          tagName="div"
        >
          <SimpleTruncate lines={2}>{title}</SimpleTruncate>
        </Title>
        {option === "workflow-agent" &&
          description &&
          (description.length > 80 ? (
            <DigTooltip title={description} autoHide={true}>
              <Text size="small" className={styles.CardDescription}>
                <SimpleTruncate lines={3}>{description}</SimpleTruncate>
              </Text>
            </DigTooltip>
          ) : (
            <Text size="small" className={styles.CardDescription}>
              <SimpleTruncate lines={3}>{description}</SimpleTruncate>
            </Text>
          ))}
      </div>
    );
  },
);
EmptyChatInstructionCard.displayName = "EmptyChatInstructionCard";

function WorkflowAgentsList({ isSmallSize }: { isSmallSize: boolean }) {
  const navigate = useNavigate();
  const currentAccount = useDropboxAccount();
  const chatRoute = useChatRoute();

  const { agents } = useWorkflowAgents();

  return (
    <>
      <div
        className={classNames(styles.TitleContainer, {
          [styles.TitleContainerMobile]: isSmallSize,
        })}
      >
        <Title
          size="medium"
          weightVariant="emphasized"
          className={styles.Title}
        >
          {i18n.t("assistant_ai_tool_title")}
        </Title>
      </div>
      <div
        className={classNames(styles.EmptyChatInstructionsCards, {
          [styles.MobileCards]: isSmallSize,
        })}
      >
        <EmptyChatInstructionCard
          key="create-ai-tool"
          option="create-ai-tool"
          isComingSoon={false}
          onClickOption={() => {
            navigate(RoutePath.NEW_WORKFLOW_AGENT);
          }}
          isMobileSize={isSmallSize}
        />
        {agents.map((agent) => (
          <EmptyChatInstructionCard
            key={agent.id}
            option="workflow-agent"
            isComingSoon={false}
            onClickOption={async () => {
              const state = buildConversationRouterState(
                CtaSource.WorkflowAgent,
                {
                  scenarioMeta: {
                    type: "workflow_agent",
                    workflowAgentId: agent.id,
                    model: agent.model,
                  },
                } satisfies WorkflowAgentParams,
              );
              navigate(chatRoute, { state });
            }}
            withCardAccessories={
              <Split gap="Micro Small" alignY="center">
                {agent && agent.accessType === "company" && (
                  <Split.Item paddingTop="2">
                    <Text>
                      <UIIcon src={PersonMultipleLine} size="small" />
                    </Text>
                  </Split.Item>
                )}
                {agent && agent.createdBy.email === currentAccount?.email && (
                  <Split.Item>
                    <IconButtonWithTooltip
                      shape="circular"
                      variant="outline"
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        navigate(
                          generatePath(RoutePath.WORKFLOW_AGENT, {
                            agentId: agent.id,
                          }),
                        );
                      }}
                      tooltipProps={{
                        title: i18n.t("edit"),
                      }}
                    >
                      <UIIcon src={EditLine} size="medium" />
                    </IconButtonWithTooltip>
                  </Split.Item>
                )}
              </Split>
            }
            isMobileSize={isSmallSize}
            agent={agent}
          />
        ))}
      </div>
    </>
  );
}

function getCardOptions(
  isWorkflowAgentsEnabled: boolean,
): EmptyChatInstructionsOption[] {
  const options: EmptyChatInstructionsOption[] = ["write", "chat_with_dash"];
  if (isWorkflowAgentsEnabled) {
    options.push("library");
  }
  return options;
}

function getCardRenderInfo(
  option: EmptyChatInstructionsOption,
  agent?: WorkflowAgent,
) {
  switch (option) {
    case "chat_with_dash":
      return {
        title: i18n.t("assistant_card_title_chat_with_dash"),
        icon: AiChatLine,
        color: "var(--dig-color__accent__cyan)",
        borderVariant: "normal",
      };
    case "write":
      return {
        title: i18n.t("assistant_card_title_write"),
        icon: EditLine,
        color: "var(--dig-color__accent__red)",
        borderVariant: "normal",
      };
    case "create-ai-tool":
      return {
        title: i18n.t("assistant_card_title_create_ai_tool"),
        description: i18n.t("assistant_card_description_create_ai_tool"),
        icon: AddCircleLine,
        color: "var(--dig-color__accent__magenta)",
        borderVariant: "normal",
      };
    case "library":
      return {
        title: i18n.t("library"),
        icon: AddToLibraryLine,
        color: "var(--dig-color__accent__green)",
        borderVariant: "normal",
      };
    case "workflow-agent": {
      if (!agent) {
        throw new Error("Workflow agent is required");
      }
      const { title, description, icon } =
        getWorkflowAgentCardRenderInfo(agent);
      return {
        title,
        description,
        // This is a backwards compatibility fix for agents that were created before we properly
        // introduced the sentinel value for the Dash custom emoji that saved with empty string value.
        icon: icon ?? "",
        color: "var(--dig-color__accent__green)",
        borderVariant: "normal",
      };
    }
    default:
      option satisfies never;
      throw new Error(`Unknown option: ${option}`);
  }
}

function getWorkflowAgentCardRenderInfo(agent: WorkflowAgent) {
  return {
    title: agent.name,
    description: agent.description,
    icon: agent.icon,
  };
}
